<template>
  <v-navigation-drawer
    v-model="drawer"
    absolute
    temporary
    style="background-color: var(--nav-drawer-background);"
  >
    <v-toolbar flat dense>
      <v-btn icon @click="close">
        <v-icon>arrow_back</v-icon>
      </v-btn>

      <v-toolbar-title class="text-sm-center" style="margin-left:10px;">
        {{title}}
      </v-toolbar-title>
    </v-toolbar>

    <profile-avatar 
      :avatar="avatar" 
      :title="name"
      :canUpload="canUpload"
      :canRemove="canRemoveAvatar"
      :chatId="chatDetails"
      :changed="avatarChanged"
      v-if="showAvatar"
    />

    <template v-for="(item, i) in items">
      <profile-item
        :key="i"
        :title="item.title"
        :value="item.value"
        :valueLength="item.valueLength"
        :readonly="item.readonly"
        :qrcode="item.qrcode === true"
        :onChanged="(value) => profileItemChanged(i, value)"
      />
    </template>

    <template v-if="isUser">
      <v-layout align-center justify-start>
        <v-btn @click="deleteAccountDialog=true" class="delete-account" depressed flat>
          {{$t('delete_account')}}</v-btn>
      </v-layout>

      <delete-account-dialog :visible.sync="deleteAccountDialog" />
    </template>

    <template v-if="isGroup">
      <div class="cd-users-list">
        <v-list v-scroll class="pa-0 unselectable">
          <v-subheader>
            {{ this.$t('call_participants', [this.users.length]) }}
          </v-subheader>

          <v-list-tile
            v-for="(item, index) in users"
            :key="index"
            style="height: 45px !important;"
          >
            <v-list-tile-avatar>
              <user-avatar 
                :avatar="item.avatar"
                :username="item.name" 
                :size="38"
              />
            </v-list-tile-avatar>

            <v-list-tile-content>
              <v-list-tile-title v-text="item.name" ></v-list-tile-title>
            </v-list-tile-content>

          </v-list-tile>
        </v-list>
      </div>
    </template>

    <call-details v-else-if="isCall" />
  
  </v-navigation-drawer>
</template>

<style>
  .cd-users-list {
    overflow-y: auto;
    background-size: 100%;
    padding: 10px 0px;
  }
  .delete-account {
    font-weight:normal !important;
    padding: 0 4px !important;
    font-size: 15px !important;
  }
  .delete-account.v-btn:hover:before {
    background-color: transparent !important;
  }
  .delete-account.v-btn:hover {
    font-weight: 500 !important;
  }
</style>

<script> 
  import ProfileAvatar from '@common/ProfileAvatar.vue'
  import ProfileItem from '@common/ProfileItem.vue'
  import UserAvatar from '@common/UserAvatar.vue'
  import CallDetails from './call/CallDetails.vue'
  import DeleteAccountDialog from '@dialogs/DeleteAccount.vue'

  import {EventBus} from '@/event-bus'

  const USER_TYPE = 0
  const CONTACT_TYPE = 1
  const CHAT_TYPE = 2
  const CALL_TYPE = 3

  export default {
    name: 'profile-drawer',
    components: {
      CallDetails,
      DeleteAccountDialog,
      ProfileItem,
      ProfileAvatar,
      UserAvatar
    },
    data() {
      return {
        drawer: false,
        title: '',
        name: '',
        avatar: '',
        type: USER_TYPE,
        items: [],
        users: [],
        deleteAccountDialog: false,
        chatId: 0
      }
    },
    mounted() {
      EventBus.$on('open-my-profile', this.openMyProfile)
      EventBus.$on('open-contact-profile', this.openContactProfile)
      EventBus.$on('open-chat-details', this.openChatDetails)
      EventBus.$on('call-open-details', this.openCallDetails)
    },
    beforeDestroy() {
      EventBus.$off('open-my-profile')
      EventBus.$off('open-contact-profile')
      EventBus.$off('open-chat-details')
      EventBus.$off('call-open-details')
    },
    computed: {
      canUpload() {
        return this.type == USER_TYPE || this.type == CHAT_TYPE
      },
      canRemoveAvatar() {
        return this.canUpload && this.avatar != ''
      },
      showAvatar() {
        return this.type != CALL_TYPE
      },
      isUser() {
        return this.type == USER_TYPE
      },
      isGroup() {
        return this.type == CHAT_TYPE
      },
      isCall() {
        return this.type == CALL_TYPE
      },
      chatDetails() {
        return this.type == CHAT_TYPE ? this.chatId : undefined
      }
    },
    methods: {
      close() {
        // TODO: reset active edit
        this.drawer = false
      },
      openMyProfile() {
        this.type = USER_TYPE
        this.title = this.$t('drawer_profile')
        this.items.splice(0)

        const p = this.$store.state.profile
        this.items = [
          { title: this.$t('appname_id'), value: p.contactId, readonly: true, qrcode: true },
          { title: this.$t('contacts_name'), value: p.name, readonly: false, key: 'name', valueLength:50 },
          { title: this.$t('acc_phone_number'), value: p.phone, readonly: false, key: 'phone', valueLength:50 },
          { title: this.$t('acc_email'), value: p.email, readonly: false, key: 'email', valueLength:100 },
          { title: this.$t('contacts_fingerprint'), value: p.fingerprint, readonly: true },
        ]
        this.avatar = p.avatar
        this.name = p.name
        this.drawer = true
      },
      openContactProfile(contactId) {
        this.type = CONTACT_TYPE
        this.title = this.$t('contacts_contact_details')
        this.items.splice(0)

        this.$store.dispatch('fetchUserProfile', contactId).then((resp) => {
          this.items = [
            { title: this.$t('appname_id'), value: contactId, readonly: true },
            { title: this.$t('contacts_name'), value: resp.name, readonly: true, key: 'name' },
            { title: this.$t('contacts_fingerprint'), value: resp.fingerprint, readonly: true },
          ]
          this.avatar = resp.avatar
          this.name = resp.name
          this.drawer = true
        })
      },
      openChatDetails(chatId) {
        let s = this.$store.state
        let chat = s.chats.find(it => it.chatId === chatId)
        if (!chat.group) {
          this.openContactProfile(chat.users[0])
          return
        }

        this.type = CHAT_TYPE
        this.chatId = chatId
        this.users.splice(0)
        chat.users.forEach(item => {
          const con = s.contacts.find(c => c.contactId == item)
          const name = con ? con.name : item
          const avatar = con ? con.avatar : ''
          this.users.push({name, avatar})
        })

        this.items = [
          { title: this.$t('chat_title'), value: chat.title, readonly: false, key: 'title', valueLength:50 },
        ]
        this.title = this.$t('chat_settings_header')
        this.avatar = chat.avatar
        this.name  = chat.title
        this.drawer = true
      },
      openCallDetails(users) {
        this.type = CALL_TYPE
        this.title = this.$t('call_participants', [users.length])
        this.items.splice(0)
        this.drawer = true
      },
      profileItemChanged(index, value) {
        console.log('-- profile: ', index, value)
        const key = this.items[index].key
        if (key === undefined)
          return

        if (this.type == CHAT_TYPE) {
          this.$store.dispatch('updateChatTitle', { chatId: this.chatId, title: value })
        } else {
          this.$store.dispatch('updateUserProfile', { key, value })
        }
      },
      avatarChanged(avatarPath) {
        ///console.log('-- ', avatarPath)
        this.avatar = avatarPath
      }
    }
  }
</script>